import { apiClient } from "@/lib/apiClient";
import { formatDateLong, formatDateTimeLong } from "@/utils";
import type { CatalogCardProps } from "@/types/component";
import { getDeliveryTypeLabel } from "@/features/education-catalog/utils";
import {
  getEnrollmentButtonConfig,
  type CatalogButtonAction,
} from "@/features/education-catalog/services/education-catalog.api";
import type { ClientEducation } from "../types/client-educations.types";

export type { CatalogButtonAction };

const CLIENT_EDUCATIONS_ME_ENDPOINT = "/api/client/educations/me";
const DEFAULT_CARD_IMAGE = "https://picsum.photos/seed/edu/400/200";

/** Backend /me response item - ClientEducationResDto */
export interface ClientEducationMeItem {
  id: string;
  name?: string;
  title?: string;
  educationName?: string;
  categories?: Array<{ id?: string; name?: string; label?: string }>;
  instructors?: Array<{
    id?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  }>;
  coverImageFileID?: string | null;
  coverImageUrl?: string | null;
  coverImage?: string;
  image?: string;
  type?: number;
  format?: string;
  deliveryType?: string;
  category?: string;
  categoryName?: string;
  durationHours?: number;
  durationMinutes?: number;
  duration?: number;
  requestCollectionEndDate?: string;
  endDate?: string;
  startDate?: string;
  enrollment?: {
    progressPercent?: number;
    status?: number;
    completedAt?: string;
    requestedAt?: string;
    [key: string]: unknown;
  } | null;
  location?: string | null;
  /** BE yeni alan: eğitim oda / sınıf adı */
  roomName?: string | null;
  /** BE yeni alan: tarih belirlendi mi (startDate && endDate computed) */
  hasScheduledDates?: boolean;
  /** BE yeni alan: talep tarihi (enrollment.requestedAt alternatifi) */
  requestDate?: string;
  exam?: { id?: string } | string | null;
  survey?: unknown;
  [key: string]: unknown;
}

export interface ClientEducationMeResponse {
  items?: ClientEducationMeItem[];
  data?: ClientEducationMeItem[];
  count?: number;
  total?: number;
}

function formatDuration(minutes?: number, durationStr?: string): string {
  if (durationStr) return String(durationStr);
  if (minutes != null) {
    if (minutes >= 60) return `${Math.round(minutes / 60)} Saat`;
    return `${minutes} Dakika`;
  }
  return "-";
}

/**
 * enrollment.status: 2=APPROVED, 3=REJECTED, 5=CANCELLED, 6=COMPLETED, 11=NOT_COMPLETED
 * Devam Eden: status 2 ve progress < 100
 * Tamamlanan: status 6 veya progress >= 100 veya status 3,5,11
 */
function isCompleted(item: ClientEducationMeItem): boolean {
  const status = item.enrollment?.status;
  const progress = item.enrollment?.progressPercent ?? 0;
  return (
    status === 6 ||
    progress >= 100 ||
    status === 3 ||
    status === 5 ||
    status === 11
  );
}

function mapToClientEducation(item: ClientEducationMeItem): ClientEducation {
  const title = item.name ?? item.title ?? item.educationName ?? "-";
  const educatorName = (() => {
    const instr = item.instructors;
    if (instr?.length) {
      const first = instr[0];
      const full = [first.firstName, first.lastName].filter(Boolean).join(" ");
      return full || (first.name ?? "-");
    }
    return (
      (item as { instructorName?: string }).instructorName ??
      (item as { instructor?: string }).instructor ??
      (item as { educatorName?: string }).educatorName ??
      "-"
    );
  })();
  const category = (() => {
    const cats = item.categories;
    if (cats?.length) return cats[0]?.name ?? cats[0]?.label ?? "-";
    return item.category ?? item.categoryName ?? "-";
  })();
  const categoryId = item.categories?.[0]?.id;
  const deliveryTypeLabel = getDeliveryTypeLabel(item.type, item.format);
  const deliveryType =
    deliveryTypeLabel !== "-"
      ? deliveryTypeLabel
      : item.deliveryType ?? item.format ?? "-";

  const rawItem = item as Record<string, unknown>;
  const durationHours =
    item.durationHours ??
    (typeof rawItem.duration_hours === "number" ? rawItem.duration_hours : 0);
  const durationMinutes =
    item.durationMinutes ??
    (typeof rawItem.duration_minutes === "number" ? rawItem.duration_minutes : 0);
  const totalMinutes = durationHours * 60 + durationMinutes;
  const duration =
    totalMinutes > 0
      ? formatDuration(totalMinutes)
      : formatDuration(
          item.durationMinutes ?? item.duration,
          typeof item.duration === "string" ? item.duration : undefined
        );

  const deadline = formatDateLong(
    item.requestCollectionEndDate ?? item.endDate ?? ""
  );
  const progress = Math.round(
    item.enrollment?.progressPercent ?? 0
  ) as number;
  const completed = isCompleted(item);

  const totalMinutesNum =
    (item.durationHours ?? 0) * 60 + (item.durationMinutes ?? 0);
  const remainingPercent = Math.max(0, 100 - progress);
  const remainingMinutes =
    totalMinutesNum > 0
      ? Math.round((totalMinutesNum * remainingPercent) / 100)
      : 0;
  const hoursLeft =
    !completed && remainingMinutes > 0
      ? remainingMinutes >= 60
        ? `${Math.round(remainingMinutes / 60)} saat kaldı`
        : `${remainingMinutes} dk kaldı`
      : undefined;

  const imageUrl = item.coverImageUrl ?? item.coverImage ?? item.image ?? DEFAULT_CARD_IMAGE
  // BE `hasScheduledDates` alanı gönderirse (bool) ona güven.
  // Alan yoksa: geri uyum için `true` varsay (BE eski sürümde bu alanı döndürmeyebilir;
  // Eğitim Taleplerim sayfası ayrı bir endpoint kullanıyor, burada gereksiz gizleme yapmayalım).
  const hasScheduledDates =
    typeof item.hasScheduledDates === "boolean"
      ? item.hasScheduledDates
      : true;
  // BE yeni sürümde top-level `requestDate` dönüyor; fallback enrollment.requestedAt
  const requestedAtRaw =
    item.requestDate ?? item.enrollment?.requestedAt ?? null;
  const requestedAt = requestedAtRaw ? formatDateLong(requestedAtRaw) : null;
  // Oda bilgisi: BE `roomName` tercih edilir, yoksa `location` (adres) fallback
  const roomInfo = hasScheduledDates
    ? (item.roomName?.trim() || item.location?.trim() || null)
    : null;
  return {
    id: item.id,
    imageFileId: imageUrl && imageUrl !== DEFAULT_CARD_IMAGE ? null : (item.coverImageFileID ?? null),
    fallbackImageUrl: imageUrl,
    title,
    educatorName,
    duration,
    durationHours,
    durationMinutes,
    deliveryType,
    type: item.type,
    category,
    categoryId,
    deadline,
    progress,
    hoursLeft,
    isCompleted: completed,
    enrollmentStatus: item.enrollment?.status ?? null,
    startDate: item.startDate,
    endDate: item.endDate,
    hasScheduledDates,
    requestedAt,
    roomInfo,
    exam: item.exam ?? null,
    survey: item.survey ?? undefined,
  };
}

function hasExamId(row: ClientEducation): boolean {
  const exam = row.exam;
  if (exam == null) return false;
  if (typeof exam === "string") return String(exam).trim() !== "";
  const id = (exam as { id?: string }).id;
  return typeof id === "string" && id.trim() !== "";
}

function hasSurveyDefined(row: ClientEducation): boolean {
  const s = row.survey;
  if (s == null) return false;
  if (typeof s === "string") return s.trim() !== "";
  return true;
}

/** Eğitimlerim sayfası için buton metni ve aksiyonu (enrollment status + exam/survey) */
export function getButtonConfigForEducation(row: ClientEducation): {
  buttonLabelKey: string;
  buttonDisabled: boolean;
  buttonAction: CatalogButtonAction;
} {
  const config = getEnrollmentButtonConfig(
    row.enrollmentStatus,
    hasExamId(row),
    hasSurveyDefined(row)
  );

  // startDate gelecekte de olsa "Detaylar" butonunu aktif bırak.
  //
  // Önceden: `startDate > now` ise buton "Tarih Bekleniyor" + disabled oluyordu;
  // kullanıcı eğitim yayınlandığı halde kartın detayına bile giremiyordu. İçerik/
  // sınav/anket erişimi zaten backend tarafında EDUCATION_NOT_YET_STARTED ile
  // gate edili ve detay sayfasında da "Eğitim X tarihinde başlayacak" bilgi
  // kartı çıkıyor — burada tekrar kilitleme gereksiz ve kafa karıştırıcı.

  return config;
}

function mapToCatalogCardProps(
  item: ClientEducation
): Omit<CatalogCardProps, "onButtonClick"> & { id: string; buttonLabelKey: string } {
  const imageUrl = item.fallbackImageUrl ?? item.image;
  const { buttonLabelKey, buttonDisabled } = getButtonConfigForEducation(item);
  // Başlangıç saati kartta da görünür: daha önce yalnız detay ekranındaydı,
  // kursiyer listeden dersin kaçta başlayacağını göremiyordu.
  const startLabel = item.startDate ? formatDateTimeLong(item.startDate) : "";
  const metaParts = [
    startLabel ? `Başlangıç: ${startLabel}` : null,
    item.requestedAt ? `Talep: ${item.requestedAt}` : null,
    item.roomInfo ? `Oda: ${item.roomInfo}` : null,
  ].filter((s): s is string => Boolean(s));
  // NOT_COMPLETED (11) → kart deadline yerine "Devamsızlıktan Kaldı" simple badge'i gösterir.
  const isNotCompleted = item.enrollmentStatus === 11;
  return {
    id: item.id,
    imageFileId: item.imageFileId ?? undefined,
    fallbackImageUrl: imageUrl ?? undefined,
    title: item.title,
    description: metaParts.length > 0 ? metaParts.join(" · ") : undefined,
    educatorName: item.educatorName,
    duration: item.duration,
    deliveryType: item.deliveryType,
    category: item.category,
    statusType: isNotCompleted ? "simple" : "deadline",
    statusText: isNotCompleted ? "clientEducations.status.notCompleted" : undefined,
    deadline: isNotCompleted ? undefined : item.deadline,
    buttonLabelKey,
    buttonDisabled,
    buttonLabel: buttonLabelKey, // sayfa t(buttonLabelKey) ile override eder
  };
}

export const clientEducationsApi = {
  getMyEducations: async (params?: {
    isCompleted?: boolean;
    search?: string;
  }): Promise<{
    data: ClientEducation[];
    total: number;
  }> => {
    const response = await apiClient.get<ClientEducationMeResponse>(
      CLIENT_EDUCATIONS_ME_ENDPOINT,
      { params: { ...params, limit: 100 } }
    );
    const resData = response.data ?? {};
    const rawItems =
      resData?.items ?? resData?.data ?? (Array.isArray(response.data) ? response.data : []);
    const items = Array.isArray(rawItems) ? rawItems : [];
    const total = resData?.count ?? resData?.total ?? items.length;

    return {
      data: items.map((e: ClientEducationMeItem) => mapToClientEducation(e)),
      total: typeof total === "number" ? total : items.length,
    };
  },

  mapToCatalogCardProps,
};
